import type * as LeasedTag from "../../lib/models/leased-tag.server.ts"
import type * as Team from "../../lib/models/team.server.ts"

import * as UI from "@iyk/ui"
import * as React from "react"

import { getSingularOrPluralTerm } from "@iyk/localization"
import { toSentenceCase } from "@iyk/string"
import { Await, Link, useNavigate } from "@remix-run/react"
import { useLang } from "../../lib/lang/use-lang.ts"

export function TeamLinkWithHoverCard(props: TeamLinkWithHoverCardProps) {
  const { team, teamOwnersCount, userItemsFromTeam } = props

  return (
    <UI.HoverCard openDelay={250} closeDelay={250}>
      <div className="flex">
        <UI.HoverCardTrigger className="flex">
          <ProfileLink team={team} />
        </UI.HoverCardTrigger>
      </div>
      <UI.HoverCardContent
        side="bottom"
        align="start"
        sideOffset={10}
        className="border border-gray-4 [box-shadow:0px_3px_12px_0px_rgba(0,0,0,0.09)] w-full min-w-[350px] rounded-none"
      >
        <CardContent
          team={team}
          teamOwnersCount={teamOwnersCount}
          userItemsFromTeam={userItemsFromTeam}
        />
      </UI.HoverCardContent>
    </UI.HoverCard>
  )
}

export function AsyncTeamLinkWithHoverCard(props: AsyncTeamLinkWithHoverCardProps) {
  return (
    <React.Suspense fallback={<ProfileLink team={props.team} />}>
      <Await resolve={props.currentUserOwnedItemsPromise}>
        {(currentUserItems) => (
          <React.Suspense fallback={<ProfileLink team={props.team} />}>
            <Await resolve={props.teamOwnersCountPromise}>
              {(teamOwnersCount) => (
                <TeamLinkWithHoverCard
                  team={props.team}
                  teamOwnersCount={teamOwnersCount}
                  userItemsFromTeam={
                    currentUserItems ? getUserItemsFromTeam(props.team.id, currentUserItems) : null
                  }
                />
              )}
            </Await>
          </React.Suspense>
        )}
      </Await>
    </React.Suspense>
  )
}

// #region Components
function ProfileLink(props: { team: Team.ProfilePreview }) {
  const { team } = props
  const navigate = useNavigate()

  return (
    <div
      className="flex items-center gap-1.5 cursor-pointer select-none"
      onClick={() => navigate(getTeamProfileUrl(team.urlSlug))}
    >
      <UI.Avatar className="size-4" variant="square" src={team.iconUrl || undefined} />
      <span className="text-xs-upper">{team.name}</span>
    </div>
  )
}

const OWNED_ITEMS_PREVIEW_COUNT = 4

function CardContent(props: TeamLinkWithHoverCardProps) {
  const { team, teamOwnersCount, userItemsFromTeam } = props
  const userItems = Object.values(userItemsFromTeam || {})
  const userItemsCount = userItems.length
  const userPreviewItems = userItems.slice(0, OWNED_ITEMS_PREVIEW_COUNT)
  const lang = useLang()

  return (
    <div className="flex flex-col gap-4 p-4">
      <Link to={getTeamProfileUrl(team.urlSlug)} className="flex items-center gap-4">
        <UI.Avatar className="size-8" variant="square" src={team.iconUrl || undefined} />
        <div className="flex flex-col gap-0.5">
          <span className="text-sm">{team.name}</span>
          <span className="text-gray-11 text-xs">
            {teamOwnersCount}{" "}
            {getSingularOrPluralTerm(
              teamOwnersCount,
              lang.terms.OWNER,
              lang.terms.OWNER__OTHER,
              lang.name,
            )}
          </span>
        </div>
      </Link>
      {userItemsCount > 0 && (
        <div className="flex items-center gap-2">
          <UI.OverlapGroup overlapInPixels={4} initialZIndex={999}>
            {userPreviewItems.map((item) => (
              <UI.Media
                key={item.productId}
                src={item.productImageUrl || UI.defaultAvatarSVG}
                alt={item.productName}
                className={UI.classForAvatar({
                  variant: "square",
                  className: "size-4 border border-gray-7",
                })}
              />
            ))}
          </UI.OverlapGroup>
          <span className="text-gray-11 text-xs">
            {toSentenceCase(lang.terms.YOU_OWN)} {userItemsCount}{" "}
            {getSingularOrPluralTerm(
              userItemsCount,
              lang.terms.ITEM,
              lang.terms.ITEM__OTHER,
              lang.name,
            )}
          </span>
        </div>
      )}
    </div>
  )
}

// #endregion

// #region Utils

function getTeamProfileUrl(urlSlug: string) {
  return `/creators/${urlSlug}`
}

function getUserItemsFromTeam(
  teamId: number,
  userItems: LeasedTag.GroupedByProduct,
): LeasedTag.GroupedByProduct {
  return Object.fromEntries(Object.entries(userItems).filter(([_, item]) => item.teamId === teamId))
}

// #endregion

// #region Types

type TeamLinkWithHoverCardProps = {
  team: Team.ProfilePreview
  teamOwnersCount: number
  userItemsFromTeam: LeasedTag.GroupedByProduct | null
}

type AsyncTeamLinkWithHoverCardProps = {
  team: TeamLinkWithHoverCardProps["team"]
  teamOwnersCountPromise: Promise<TeamLinkWithHoverCardProps["teamOwnersCount"]>
  currentUserOwnedItemsPromise: Promise<TeamLinkWithHoverCardProps["userItemsFromTeam"]>
}

// #endregion
